/**
 * Définition des tags disponibles dans les modèles d'email.
 *
 * Syntaxe dans les corps/sujets : {{namespace.champ}} ou {{namespace.champ|valeur par défaut}}
 *
 * Les tags pilote/copilote ne sont résolus que quand l'email est envoyé depuis
 * une fiche équipage. Pour les autres contextes, ils sont remplacés par "".
 */

import type { EmailTemplateScope } from '@/types/emailTemplate';

/**
 * Visibilité d'un template selon la fiche d'où part l'email.
 * Le scope 'contact' couvre les fiches contact ET prospect (cf. migration
 * 00146 : « visible uniquement depuis une fiche contact / prospect »).
 */
export function isTemplateVisibleFor(
  scope: EmailTemplateScope,
  entityType: 'contact' | 'crew' | 'prospect'
): boolean {
  if (scope === 'all') return true;
  if (scope === 'contact') return entityType === 'contact' || entityType === 'prospect';
  return scope === entityType;
}

export interface EmailTag {
  /** Ex: "{{contact.prenom}}" */
  tag: string;
  /** Libellé affiché dans la liste de sélection */
  label: string;
}

export interface EmailTagCategory {
  id: string;
  label: string;
  /** Note contextuelle affichée sous le titre de la catégorie */
  note?: string;
  tags: EmailTag[];
}

const CONTACT_TAGS: EmailTagCategory = {
  id: 'contact',
  label: 'Contact',
  tags: [
    { tag: '{{contact.prenom}}', label: 'Prénom' },
    { tag: '{{contact.nom}}', label: 'Nom' },
    { tag: '{{contact.nom_complet}}', label: 'Nom complet' },
    { tag: '{{contact.email}}', label: 'Email' },
    { tag: '{{contact.telephone}}', label: 'Téléphone' },
  ],
};

const CREW_TAGS: EmailTagCategory = {
  id: 'equipage',
  label: 'Équipage',
  tags: [
    { tag: '{{equipage.numero}}', label: "Numéro d'équipage" },
    { tag: '{{equipage.nom}}', label: "Nom de l'équipage" },
  ],
};

const VEHICLE_TAGS: EmailTagCategory = {
  id: 'vehicule',
  label: 'Véhicule',
  tags: [{ tag: '{{vehicule.immatriculation}}', label: 'Immatriculation' }],
};

const PILOT_TAGS: EmailTagCategory = {
  id: 'pilote',
  label: 'Pilote',
  note: 'Disponible uniquement depuis une fiche équipage',
  tags: [
    { tag: '{{pilote.prenom}}', label: 'Prénom' },
    { tag: '{{pilote.nom}}', label: 'Nom' },
    { tag: '{{pilote.nom_complet}}', label: 'Nom complet' },
    { tag: '{{pilote.email}}', label: 'Email' },
    { tag: '{{pilote.telephone}}', label: 'Téléphone' },
  ],
};

const COPILOT_TAGS: EmailTagCategory = {
  id: 'copilote',
  label: 'Copilote',
  note: 'Disponible uniquement depuis une fiche équipage',
  tags: [
    { tag: '{{copilote.prenom}}', label: 'Prénom' },
    { tag: '{{copilote.nom}}', label: 'Nom' },
    { tag: '{{copilote.nom_complet}}', label: 'Nom complet' },
    { tag: '{{copilote.email}}', label: 'Email' },
    { tag: '{{copilote.telephone}}', label: 'Téléphone' },
  ],
};

const PRODUIT_TAGS: EmailTagCategory = {
  id: 'produit',
  label: 'Produit',
  tags: [
    { tag: '{{produit.nom}}', label: 'Nom du produit' },
    { tag: '{{produit.date_debut}}', label: 'Date de début' },
    { tag: '{{produit.date_fin}}', label: 'Date de fin' },
  ],
};

/**
 * Bulletin d'engagement (migration 00233). Résolus par les automatisations
 * BED et, depuis 00297, par « Envoyer un email » pour un admin depuis une fiche
 * équipage ou contact (le lien de reprise ne part qu'aux membres de l'équipage).
 */
const BED_TAGS: EmailTagCategory = {
  id: 'bed',
  label: "Bulletin d'engagement",
  note: "Relances et confirmations automatiques, ou envoi par un admin depuis la fiche (lien de reprise : membres de l'équipage seulement)",
  tags: [
    { tag: '{{bed.statut}}', label: 'Statut du BED' },
    {
      tag: '{{bed.elements_manquants}}',
      label: 'Éléments manquants (texte, avec pilote/copilote)',
    },
    { tag: '{{bed.nb_elements_manquants}}', label: "Nombre d'éléments manquants" },
    { tag: '{{bed.date_promise}}', label: 'Date promise la plus proche' },
    { tag: '{{bed.date_limite_infos}}', label: 'Date limite de communication des infos' },
    { tag: '{{bed.montant_total}}', label: 'Montant total du solde' },
    { tag: '{{bed.montant_paye}}', label: 'Montant déjà versé' },
    { tag: '{{bed.montant_restant}}', label: 'Montant restant à payer' },
  ],
};

const BLOC_TAGS: EmailTagCategory = {
  id: 'bloc',
  label: 'Blocs',
  note: 'Insère un bloc HTML complet — à placer seul dans un paragraphe',
  tags: [
    { tag: '{{bloc.modes_paiement}}', label: 'Modes de paiement (virement + chèque)' },
    {
      tag: '{{bloc.elements_manquants}}',
      label: 'Liste des éléments manquants (puces, avec rôle et date promise)',
    },
  ],
};

const LIEN_TAGS: EmailTagCategory = {
  id: 'lien',
  label: 'Liens',
  note: 'Si le document est absent, le lien et son texte sont automatiquement supprimés',
  tags: [
    { tag: '{{lien.bpi_repli}}', label: 'BPI repli (PDF)' },
    {
      tag: '{{lien.ajouter_coequipier}}',
      label: 'Ajouter un coéquipier (disponible si équipage incomplet)',
    },
    {
      tag: '{{lien.bed_reprise}}',
      label: 'Reprendre le bulletin / le paiement (lien BED tokenisé)',
    },
  ],
};

const CREW_LIEN_TAGS: EmailTagCategory = {
  ...LIEN_TAGS,
  // La note ÉTEND celle de LIEN_TAGS (suppression des liens vides) : elle
  // partage l'id 'lien', donc elle la remplace partout où elle est proposée.
  note: 'Si le document est absent, le lien et son texte sont automatiquement supprimés. Le lien BED est créé ou réutilisé à l’envoi (équipage uniquement)',
  tags: [
    ...LIEN_TAGS.tags,
    {
      tag: '{{lien.bed}}',
      label: 'Paiement BED (créé ou réutilisé à l’envoi)',
    },
  ],
};

/** Toutes les catégories — pour l'édition de modèles (contexte inconnu). */
export const ALL_TAG_CATEGORIES: EmailTagCategory[] = [
  CONTACT_TAGS,
  CREW_TAGS,
  VEHICLE_TAGS,
  PRODUIT_TAGS,
  PILOT_TAGS,
  COPILOT_TAGS,
  BED_TAGS,
  BLOC_TAGS,
  CREW_LIEN_TAGS,
];

/** Catégories disponibles selon le type d'entité depuis laquelle l'email est envoyé. */
export function getTagsByEntityType(
  entityType: 'contact' | 'crew' | 'prospect'
): EmailTagCategory[] {
  switch (entityType) {
    case 'crew':
      return [
        CREW_TAGS,
        VEHICLE_TAGS,
        PRODUIT_TAGS,
        PILOT_TAGS,
        COPILOT_TAGS,
        BED_TAGS,
        BLOC_TAGS,
        CREW_LIEN_TAGS,
      ];
    case 'contact':
    case 'prospect':
      return [CONTACT_TAGS, CREW_TAGS, VEHICLE_TAGS, PRODUIT_TAGS, BLOC_TAGS, LIEN_TAGS];
    default:
      return ALL_TAG_CATEGORIES;
  }
}
